import React, { useState, useEffect } from 'react';
import './Menu.css';
import BASE from '../api';

function isSeasonallyUnavailable(description) {
  if (!description) return false;
  const match = description.match(/(\d+)[–\-~](\d+)月停售/);
  if (!match) return false;
  const month = new Date().getMonth() + 1;
  return month >= parseInt(match[1]) && month <= parseInt(match[2]);
}

function buildCartItem(item, size) {
  const price = size === 'S' ? item.price_small : item.price;
  const labelL = item.label_large || '大';
  const labelS = item.label_small || '小';
  const suffix = size === 'M' ? ` (${labelL})` : size === 'S' ? ` (${labelS})` : '';
  return {
    id: item.id,
    cartKey: size ? `${item.id}_${size}` : String(item.id),
    displayName: item.name + suffix,
    price,
    size: size || null,
  };
}

function buildTempCartItem(item, temp) {
  return {
    id: item.id,
    cartKey: `${item.id}_${temp}`,
    displayName: `${item.name} (${temp})`,
    price: item.price,
    size: temp,
  };
}

function AddBtn({ onClick }) {
  const [active, setActive] = useState(false);
  return (
    <button
      className={`add-btn${active ? ' add-btn-active' : ''}`}
      onClick={() => {
        onClick();
        setActive(true);
        setTimeout(() => setActive(false), 500);
      }}
    >加入</button>
  );
}

function DescriptionLine({ text }) {
  if (!text) return null;
  const parts = text.split('｜');
  return (
    <div className="item-desc">
      {parts.map((part, i) => (
        <span key={i} className={i > 0 ? 'item-desc-tag' : 'item-desc-main'}>{part}</span>
      ))}
    </div>
  );
}

function CrownIcon() {
  const gold = '#E8B84B';
  const dark = '#B07820';
  const band = '#D4A030';
  return (
    <svg className="crown-icon" viewBox="0 0 100 78" xmlns="http://www.w3.org/2000/svg">
      {/* Crown body */}
      <path
        d="M8 73
           C7 65 6 57 8 50
           C9 45 12 37 15 33
           C18 37 21 44 24 48
           C27 44 30 30 35 22
           C38 30 41 38 44 42
           C46 34 48 18 50 8
           C52 18 54 34 56 42
           C59 38 62 30 65 22
           C70 30 73 44 76 48
           C79 44 82 37 85 33
           C88 37 91 45 92 50
           C94 57 93 65 92 73 Z"
        fill={gold}
      />
      {/* Dark shadow triangles between inner peaks */}
      <path d="M24 48 C27 44 30 30 35 22 C38 30 41 38 44 42 Z" fill={dark}/>
      <path d="M56 42 C59 38 62 30 65 22 C70 30 73 44 76 48 Z" fill={dark}/>
      {/* Base band */}
      <path d="M8 63 L92 63 L92 73 C92 76 8 76 8 73 Z" fill={band}/>
      <line x1="9" y1="63.5" x2="91" y2="63.5" stroke={dark} strokeWidth="1.5"/>
      {/* Center oval gem */}
      <ellipse cx="50" cy="52" rx="9" ry="5.5" fill={dark}/>
      {/* 5 balls */}
      <circle cx="15" cy="33" r="6"  fill={gold} stroke={dark} strokeWidth="2"/>
      <circle cx="35" cy="22" r="7"  fill={gold} stroke={dark} strokeWidth="2"/>
      <circle cx="50" cy="8"  r="8"  fill={gold} stroke={dark} strokeWidth="2"/>
      <circle cx="65" cy="22" r="7"  fill={gold} stroke={dark} strokeWidth="2"/>
      <circle cx="85" cy="33" r="6"  fill={gold} stroke={dark} strokeWidth="2"/>
    </svg>
  );
}

function TempBadge({ temperature }) {
  if (!temperature || temperature === 'both') return null;
  return (
    <span className={`temp-fixed-badge temp-${temperature}`}>
      {temperature === 'ice' ? '冰' : '熱'}
    </span>
  );
}


function ItemCard({ item, onAddToCart, onUpdateQuantity, cartMap, onOpenDrinkSelect, onOpenComboSelect, onOpenFlavorSelect }) {
  const seasonallyUnavailable = isSeasonallyUnavailable(item.description);
  const hasFlavorOptions = !!item.combo_options && !item.combo_options.includes(':');
  const hasComboOptions = !!item.combo_options && item.combo_options.includes(':');
  const hasDrinkOptions = !!item.drink_options;
  const hasVariantOptions = !!item.variant_options;
  const hasSizes = item.price_small != null;
  const hasTempChoice = item.temperature === 'both';

  const qtyM = cartMap[`${item.id}_M`] || 0;
  const qtyS = cartMap[`${item.id}_S`] || 0;
  const qtySingle = cartMap[String(item.id)] || 0;
  const qtyIce = cartMap[`${item.id}_冰`] || 0;
  const qtyHot = cartMap[`${item.id}_熱`] || 0;
  const labelL = item.label_large || '大';
  const labelS = item.label_small || '小';

  const totalSpecialQty = (hasComboOptions || hasFlavorOptions)
    ? Object.entries(cartMap).filter(([k]) => k === String(item.id) || k.startsWith(`${item.id}_`)).reduce((s, [, v]) => s + v, 0)
    : 0;

  const drinkCartKeys = hasDrinkOptions
    ? (item.drink_options.split('|').flatMap(opt =>
        opt === '紅茶' ? [`${item.id}_紅茶冰`, `${item.id}_紅茶熱`] : [`${item.id}_${opt}`]
      ))
    : [];
  const totalDrinkQty = drinkCartKeys.reduce((sum, k) => sum + (cartMap[k] || 0), 0);

  if (seasonallyUnavailable) {
    return (
      <div className="item-card item-card-unavailable">
        <div className="item-body">
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
            </div>
            <span className="seasonal-badge">本季停售</span>
            <span className="item-price">${item.price}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="item-card">
      <div className="item-body">
        {hasFlavorOptions ? (
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
              <DescriptionLine text={item.description} />
            </div>
            {totalSpecialQty > 0 ? (
              <button className="drink-select-btn drink-select-btn-active" onClick={() => onOpenFlavorSelect(item)}>
                已選 {totalSpecialQty} 份
              </button>
            ) : (
              <AddBtn onClick={() => onOpenFlavorSelect(item)} />
            )}
            <span className="item-price">${item.price}</span>
          </div>
        ) : hasComboOptions ? (
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
              <DescriptionLine text={item.description} />
            </div>
            {totalSpecialQty > 0 ? (
              <button className="drink-select-btn drink-select-btn-active" onClick={() => onOpenComboSelect(item)}>
                已選 {totalSpecialQty} 份
              </button>
            ) : (
              <AddBtn onClick={() => onOpenComboSelect(item)} />
            )}
            <span className="item-price">${item.price}</span>
          </div>
        ) : hasDrinkOptions ? (
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
              <DescriptionLine text={item.description} />
            </div>
            {totalDrinkQty > 0 ? (
              <button className="drink-select-btn drink-select-btn-active" onClick={() => onOpenDrinkSelect(item)}>
                已選 {totalDrinkQty} 份
              </button>
            ) : (
              <AddBtn onClick={() => onOpenDrinkSelect(item)} />
            )}
            <span className="item-price">${item.price}</span>
          </div>
        ) : hasVariantOptions ? (() => {
          const variants = item.variant_options.split('|');
          return (
            <div className="item-row">
              <div className="item-name-wrap">
                <div className="item-name-row">
                  {item.featured === 1 && <CrownIcon />}
                  <span className="item-name">{item.name}</span>
                </div>
                <DescriptionLine text={item.description} />
              </div>
              <div className="temp-btns">
                {variants.map(v => {
                  const key = `${item.id}_${v}`;
                  const qty = cartMap[key] || 0;
                  return (
                    <div key={v} className="temp-btn-group">
                      <span className="temp-label">{v}</span>
                      {qty > 0 ? (
                        <div className="qty-ctrl">
                          <button onClick={() => onUpdateQuantity(key, -1)}>−</button>
                          <span>{qty}</span>
                          <button onClick={() => onAddToCart({ id: item.id, cartKey: key, displayName: `${item.name}(${v})`, price: item.price, size: null })}>+</button>
                        </div>
                      ) : (
                        <AddBtn onClick={() => onAddToCart({ id: item.id, cartKey: key, displayName: `${item.name}(${v})`, price: item.price, size: null })} />
                      )}
                    </div>
                  );
                })}
              </div>
              <span className="item-price">${item.price}</span>
            </div>
          );
        })() : hasSizes ? (
          <div className="size-rows">
            <div className="item-row">
              <div className="item-name-wrap">
                <div className="item-name-row">
                  {item.featured === 1 && <CrownIcon />}
                  <span className="item-name">{item.name}</span>
                </div>
                <DescriptionLine text={item.description} />
              </div>
              <span className="size-label">{labelS}</span>
              {qtyS > 0 ? (
                <div className="qty-ctrl">
                  <button onClick={() => onUpdateQuantity(`${item.id}_S`, -1)}>−</button>
                  <span>{qtyS}</span>
                  <button onClick={() => onAddToCart(buildCartItem(item, 'S'))}>+</button>
                </div>
              ) : (
                <AddBtn onClick={() => onAddToCart(buildCartItem(item, 'S'))} />
              )}
              <span className="item-price">${item.price_small}</span>
            </div>
            <div className="item-row item-row-sub">
              <span className="item-name-placeholder" />
              <span className="size-label">{labelL}</span>
              {qtyM > 0 ? (
                <div className="qty-ctrl">
                  <button onClick={() => onUpdateQuantity(`${item.id}_M`, -1)}>−</button>
                  <span>{qtyM}</span>
                  <button onClick={() => onAddToCart(buildCartItem(item, 'M'))}>+</button>
                </div>
              ) : (
                <AddBtn onClick={() => onAddToCart(buildCartItem(item, 'M'))} />
              )}
              <span className="item-price">${item.price}</span>
            </div>
          </div>
        ) : hasTempChoice ? (
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
              <DescriptionLine text={item.description} />
            </div>
            <div className="temp-btns">
              <div className="temp-btn-group">
                <span className="temp-label temp-ice">冰</span>
                {qtyIce > 0 ? (
                  <div className="qty-ctrl">
                    <button onClick={() => onUpdateQuantity(`${item.id}_冰`, -1)}>−</button>
                    <span>{qtyIce}</span>
                    <button onClick={() => onAddToCart(buildTempCartItem(item, '冰'))}>+</button>
                  </div>
                ) : (
                  <AddBtn onClick={() => onAddToCart(buildTempCartItem(item, '冰'))} />
                )}
              </div>
              <div className="temp-btn-group">
                <span className="temp-label temp-hot">熱</span>
                {qtyHot > 0 ? (
                  <div className="qty-ctrl">
                    <button onClick={() => onUpdateQuantity(`${item.id}_熱`, -1)}>−</button>
                    <span>{qtyHot}</span>
                    <button onClick={() => onAddToCart(buildTempCartItem(item, '熱'))}>+</button>
                  </div>
                ) : (
                  <AddBtn onClick={() => onAddToCart(buildTempCartItem(item, '熱'))} />
                )}
              </div>
            </div>
            <span className="item-price">${item.price}</span>
          </div>
        ) : (
          <div className="item-row">
            <div className="item-name-wrap">
              <div className="item-name-row">
                {item.featured === 1 && <CrownIcon />}
                <span className="item-name">{item.name}</span>
              </div>
              <DescriptionLine text={item.description} />
            </div>
            <TempBadge temperature={item.temperature} />
            {qtySingle > 0 ? (
              <div className="qty-ctrl">
                <button onClick={() => onUpdateQuantity(String(item.id), -1)}>−</button>
                <span>{qtySingle}</span>
                <button onClick={() => onAddToCart(buildCartItem(item, null))}>+</button>
              </div>
            ) : (
              <AddBtn onClick={() => onAddToCart(buildCartItem(item, null))} />
            )}
            <span className="item-price">${item.price}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Menu({ onAddToCart, onUpdateQuantity, cart, onOpenDrinkSelect, onOpenComboSelect, onOpenFlavorSelect }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/api/menu`)
      .then(r => r.json())
      .then(data => {
        setCategories(data);
        if (data.length > 0) setActiveCat(data[0].id);
        setLoading(false);
      })
      .catch(() => {
        setError('無法載入菜單，請確認後端服務是否正常運行。');
        setLoading(false);
      });
  }, []);

  const cartMap = {};
  cart.forEach(c => { cartMap[c.cartKey] = c.quantity; });

  if (loading) return <div className="status-msg">載入菜單中...</div>;
  if (error) return <div className="status-msg error">{error}</div>;

  const activeCategory = categories.find(c => c.id === activeCat);

  return (
    <div className="menu">
      <div className="cat-tabs">
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`cat-tab ${activeCat === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCat(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {activeCategory?.name === '找餐點' && (
        <div className="cat-notice">
          ※ 小鳥胃套餐 +60 附紅茶、脆薯，改點其它飲品折抵10元
        </div>
      )}

      <div className="items-grid">
        {activeCategory?.items.map(item => (
          <ItemCard
            key={item.id}
            item={item}
            onAddToCart={onAddToCart}
            onUpdateQuantity={onUpdateQuantity}
            cartMap={cartMap}
            onOpenDrinkSelect={onOpenDrinkSelect}
            onOpenComboSelect={onOpenComboSelect}
            onOpenFlavorSelect={onOpenFlavorSelect}
          />
        ))}
      </div>
    </div>
  );
}
